import { type RefObject, useEffect, useRef, useState } from "react";

export interface UseScrollSentinelOptions {
  /**
   * `"top"` keeps reporting `true` while the sentinel sits *below* the viewport
   * and only flips to `false` once it has left through the top edge — for
   * "has the reader scrolled past this?" checks that must not trigger on tall
   * pages where the sentinel starts below the fold. Default counts any
   * non-intersection.
   */
  position?: "top" | "bottom";
  root?: RefObject<Element | null>;
  threshold?: number;
  rootMargin?: string;
}

type UseScrollSentinelReturn = readonly [RefObject<HTMLDivElement | null>, boolean];

/**
 * Uses IntersectionObserver to detect scroll position via a sentinel element.
 * Returns [sentinelRef, isIntersecting].
 */
export function useScrollSentinel(options: UseScrollSentinelOptions = {}): UseScrollSentinelReturn {
  const { position, threshold = 0.1, root, rootMargin } = options;
  const [isIntersecting, setIsIntersecting] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // position "top" extends the observed region far below the viewport, so the
  // sentinel "intersects" anywhere at or below the top edge and only stops
  // intersecting once it leaves through the top. Deriving this from the entry's
  // boundingClientRect instead would go stale: a zero-height sentinel can cross
  // the whole viewport within one frame (fast flick, Home/End, instant jumps),
  // which is no intersection-status change at all — the observer never fires.
  const effectiveRootMargin = rootMargin ?? (position === "top" ? "0px 0px 100000px 0px" : undefined);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setIsIntersecting(entry.isIntersecting);
      },
      { threshold, root: root?.current ?? undefined, rootMargin: effectiveRootMargin },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [threshold, root, effectiveRootMargin]);

  return [sentinelRef, isIntersecting] as const;
}
